import {
    Injectable,
    NgZone,
} from '@angular/core';
import {
    AppStateFacade,
    Setting,
} from '@app-state';
import {
    FeatureFlag,
    IAlert,
    IJobUpdated,
    ILicenseUpdated,
    IRobotLicenseUpdate,
    ISessionUpdate,
} from '@models';
import { TranslateService } from '@ngx-translate/core';
import {
    IHealthStateUpdate,
    IHealthStateUpdateEvent,
    ILiveFeedMessage,
} from '@pages/monitoring/dashboard/widgets/generics';
import { WidgetResource } from '@pages/monitoring/types';
import { ServerConfiguration } from '@services/configuration/server.configuration';
import {
    FOLDER_HEADER_NAME,
    FOLDER_PARAM_NAME,
    LOCALIZATION_HEADER_NAME,
    LOCALIZATION_PARAM_NAME,
} from '@services/generics/constants';
import { Mapper } from '@services/generics/data.mapper';
import { Logger } from '@services/logger/abstract.logger';
import { toDate } from '@utils/object.utils';

import {
    Backoff,
    fibonacci,
} from 'backoff';
import cloneDeep from 'lodash-es/cloneDeep';
import difference from 'lodash-es/difference';
import intersection from 'lodash-es/intersection';
import {
    BehaviorSubject,
    forkJoin,
    fromEvent,
    merge,
    NEVER,
    Subject,
} from 'rxjs';
import {
    filter,
    map,
    pairwise,
    switchMap,
    takeUntil,
    tap,
} from 'rxjs/operators';

import { MapperService } from './mapper.service';
import { Types } from './signalr.types';

declare const jQuery: Types.JQuery;
export interface IConnection {
    hub: string;
    socket: SignalR.Hub.Connection | null;
    proxy: SignalR.Hub.Proxy | null;
    connected$: Subject<boolean>;
    isDisconnected: boolean;
    backoff: Backoff;
    // TODO: refactor to have watched array/set per connection
    shouldReWatch: boolean;
}

export interface IConnectionDefinition {
    browser: IConnection;
    monitoring: IConnection;
    callbacks: {
        onDisconnect: (connection: IConnection) => void,
        onStart: (connection: IConnection) => void,
    };
}

const enum SignalRState {
    Connecting = 0,
    Connected = 1,
    Reconnecting = 2,
    Disconnected = 4,
}

const RETRY_AFTER = 300;
const RETRY_AFTER_MAX = 60 * 1000;
const RETRY_BACKOFF_CONFIG = {
    randomisationFactor: .1,
    initialDelay: RETRY_AFTER,
    maxDelay: RETRY_AFTER_MAX,
};

interface IPermissionsUpdated {
    eventTime: Date;
    tenantId: number;
    userIds: number[];
}

// protect from Azure SignalR poisioned messages
jQuery.signalR.prototype.received = function (callback: Function) {
    const connection = this;
    jQuery(connection).bind(jQuery.signalR.events.onReceived, function (_, data) {
        try {
            callback.call(connection, data);
        } catch (e) {
            console.error(e);
        }
    });
    return connection;
};

@Injectable({
    providedIn: 'root',
})
export class SignalRService extends Mapper<ISessionUpdate | IJobUpdated | IRobotLicenseUpdate | ILicenseUpdated> {
    public jobs$: Subject<IJobUpdated> = new Subject();
    public sessions$: Subject<ISessionUpdate> = new Subject();
    public robotLicenses$: Subject<IRobotLicenseUpdate> = new Subject();
    public license$: Subject<void> = new Subject();
    public licenseUsage$: Subject<ILicenseUpdated> = new Subject();
    public notification$: Subject<IAlert> = new Subject();
    public livefeedMessages$: Subject<ILiveFeedMessage[]> = new Subject();
    public healthState$: Subject<IHealthStateUpdateEvent> = new Subject();

    private _robots: number[] = [];
    private _jobs: number[] = [];
    private _watched = new Set<string>();
    private _disconnected$ = new Subject<void>();
    private _monitoringFilter: Record<string, any> | null = null;

    private get _tenantId(): number {
        return this._appState.user.value!.tenancyId;
    }

    private get _folderId(): number {
        return this._appState.user.folder.value!.id;
    }

    public get watched(): Set<string> {
        return cloneDeep(this._watched);
    }

    public set watched(watched: Set<string>) {
        this._watched = watched;
    }

    // connection definitions
    private _connections: IConnectionDefinition = {
        browser: {
            hub: 'browserSignalrEventHub',
            socket: null,
            proxy: null,
            connected$: new Subject<boolean>(),
            isDisconnected: true,
            backoff: fibonacci(RETRY_BACKOFF_CONFIG),
            shouldReWatch: true,
        },
        monitoring: {
            hub: 'monitoringHub',
            socket: null,
            proxy: null,
            connected$: new BehaviorSubject<boolean>(false),
            isDisconnected: true,
            backoff: fibonacci(RETRY_BACKOFF_CONFIG),
            shouldReWatch: true,
        },
        callbacks: {
            onDisconnect: (connection: IConnection) => {
                // don't reconnect on force close
                if (connection.isDisconnected) {
                    this._logger.log('SignalR flagged to disconnect. Skipping retry...');
                    connection.backoff.reset();
                } else {
                    try {
                        connection.backoff.backoff();
                    } catch (e) {
                        // the backoff() method will only throw if there's already a backoff
                        // operation in progress, case in which we don't need to do anything
                        this._logger.error('SignalR already has a backoff in progress, ignoring...', e);
                    }
                }
            },
            onStart: (connection: IConnection) => {
                // the disconnectTimeout is set to 30s by the server,
                // therefore we override it after the connection starts.
                // by setting it to 0 we override SignalR's default reconnect spamming
                connection.socket!.disconnectTimeout = 0;
                if (!connection.isDisconnected) {
                    connection.backoff.reset();
                    if (connection.shouldReWatch) {
                        this._watch(Array.from(this._watched));
                    }
                }

                connection.connected$.next(true);
                connection.isDisconnected = false;

                if (connection.hub === 'monitoringHub' && this._monitoringFilter) {
                    this.monitoringSubscribe(this._monitoringFilter);
                }
            },
        },
    };

    constructor(
        _serverConfig: ServerConfiguration,
        private _appState: AppStateFacade,
        private _logger: Logger,
        private _ngZone: NgZone,
        private _translateService: TranslateService,
    ) {
        super();

        const normalizedBaseUrl = _serverConfig.baseHref.endsWith('/') ?
            _serverConfig.baseHref.substring(0, _serverConfig.baseHref.length - 1) :
            _serverConfig.baseHref;

        const monitoringUrl = normalizedBaseUrl + this._appState.settings.get(Setting.monitoringSignalrUrl);

        this._connections.browser.socket = jQuery.hubConnection(normalizedBaseUrl);
        this._connections.monitoring.socket = jQuery.hubConnection(monitoringUrl, { useDefaultPath: false });

        this._connections.browser.socket.disconnected(() => _ngZone.run(
            () => this._connections.callbacks.onDisconnect(this._connections.browser),
        ));
        this._connections.monitoring.socket.disconnected(() => _ngZone.run(
            () => this._connections.callbacks.onDisconnect(this._connections.monitoring),
        ));

        this._connections.browser.proxy = this._connections.browser.socket.createHubProxy('browserSignalrEventHub');
        this._connections.monitoring.proxy = this._connections.monitoring.socket.createHubProxy('monitoringHub');

        this._connections.browser.backoff.on('ready', (_number, delay) => _ngZone.run(
            () => this._onBackOffReady(this._connections.browser, delay),
        ));
        this._connections.monitoring.backoff.on('ready', (_number, delay) => _ngZone.run(
            () => this._onBackOffReady(this._connections.monitoring, delay),
        ));
    }

    /**
     * Initiate the signalR connections
     * and hook the appropiate event handlers
     * @memberof SignalRService
     */
    public connect() {
        if (this._appState.user.loggedInAsHost) { return; }

        if (
            !this._connections.browser.proxy ||
            !this._connections.monitoring.proxy ||
            !this._connections.browser.socket
        ) { throw new Error('Unable to connect this._connections'); }

        // browser hub events
        const browserProxy = this._connections.browser.proxy as any;
        const updateSessions$ = fromEvent<ISessionUpdate>(browserProxy, 'updateSessions').pipe(
            map(message => toDate(this._toDto(message, MapperService.sessionUpdatedMap) as ISessionUpdate)),
            tap(this._nextInZone(this.sessions$)),
        );

        const updateRobotLicenses$ = fromEvent<IRobotLicenseUpdate>(browserProxy, 'updateRobotLicenses').pipe(
            map(message => toDate(message)),
            tap(this._nextInZone(this.robotLicenses$)),
        );

        const updateLicense$ = fromEvent(browserProxy, 'updateLicense').pipe(
            tap(() => this._ngZone.run(() => this.license$.next())),
        );

        const updateLicenses$ = fromEvent<ILicenseUpdated>(browserProxy, 'updateLicenses').pipe(
            map(message => toDate(message)),
            tap(this._nextInZone(this.licenseUsage$)),
        );

        const updateJobs$ = fromEvent<IJobUpdated>(browserProxy, 'updateJobs').pipe(
            map(message => toDate(message)),
            tap(this._nextInZone(this.jobs$)),
        );

        const updatePermissions$ = fromEvent<IPermissionsUpdated>(browserProxy, 'updatePermissions').pipe(
            filter(() => !!this._appState.user.value),
            filter((message) => !message.userIds || message.userIds.indexOf(this._appState.user.value!.id) !== -1),
            switchMap(() => forkJoin([
                this._appState.user.permissions.fetch(),
                this._appState.user.folder.hasAny ?
                    NEVER :
                    // the list of folders might have changed
                    this._appState.user.folder.fetch(),
            ])),
        );

        // abp common events
        const getNotification$ = fromEvent<any>(browserProxy, 'getNotification').pipe(
            map(message => {
                const notification = message.notification;
                notification.message = notification.data.message;
                notification.name = notification.notificationName;
                notification.timestamp = notification.creationTime;
                return notification as IAlert;
            }),
            tap(this._nextInZone(this.notification$)),
        );

        // monitoring events
        const monitoringProxy = this._connections.monitoring.proxy as any;

        const onLiveFeedMessage$ = fromEvent<ILiveFeedMessage[]>(monitoringProxy, 'onLiveFeedMessage').pipe(
            tap(this._nextInZone(this.livefeedMessages$)),
        );

        const healthStateUpdates$ = [
            { event: 'onRobotsHealthStateChanged', target: WidgetResource.Robot },
            { event: 'onJobsHealthStateChanged', target: WidgetResource.Job },
            { event: 'onQueuesHealthStateChanged', target: WidgetResource.Queue },
        ].map(({ target, event }) =>
            fromEvent<IHealthStateUpdate>(monitoringProxy, event).pipe(
                map(message => toDate(message)),
                tap(update => this._ngZone.run(() => this.healthState$.next({
                    target,
                    update,
                }))),
            ),
        );

        merge(
            updateSessions$,
            updateRobotLicenses$,
            updateLicense$,
            updateLicenses$,
            updateJobs$,
            updatePermissions$,
            getNotification$,
            onLiveFeedMessage$,
            ...healthStateUpdates$,
        ).pipe(
            takeUntil(this._disconnected$),
        ).subscribe();

        this._connections.browser.socket.start().done(() => this._ngZone.run(() =>
            this._connections.callbacks.onStart(this._connections.browser),
        ));
        this.monitoringConnect(this._translateService.currentLang);

        this._appState.user.folder.value$
            .pipe(
                pairwise(),
                filter(([oldOu, newOu]) => !!oldOu && !!newOu),
                takeUntil(this._disconnected$),
            )
            .subscribe(([oldOu, newOu]) => {
                const watched = Array.from(this._watched);
                const toUnsubscribe = watched.filter(topic => topic.startsWith(`${this._tenantId}_${oldOu!.id}_`));
                const toSubscribe = toUnsubscribe.map(topic => topic.replace(
                    `${this._tenantId}_${oldOu!.id}_`,
                    `${this._tenantId}_${newOu!.id}_`),
                );
                this._watch(toUnsubscribe, false);
                this._watch(toSubscribe, true);
            });
    }

    /**
     * Stop all active signalR connections
     * and complete all streams
     * @memberof SignalRService
     */
    public disconnect() {
        this._robots = [];
        this._jobs = [];
        this._watched.clear();

        this._disconnected$.next();

        if (
            this._connections.browser.socket &&
            this._connections.browser.socket.state === SignalRState.Connected
        ) {
            this._connections.browser.isDisconnected = true;
            this._connections.browser.socket.stop();
        }

        if (
            this._connections.monitoring.socket &&
            this._connections.monitoring.socket.state === SignalRState.Connected
        ) {
            this._connections.monitoring.isDisconnected = true;
            this._connections.monitoring.socket.stop();
        }
    }

    /**
     * Start/stop watching robot groups
     * Removed/added groups are automatically determined
     * @param {(number[] | false)} ids
     * The robot identifiers that need to be watched.
     * Passing a falsy value will terminate all active robot subscriptions.
     * @memberof SignalRService
     */
    public watchRobots(ids: number[] | false) {
        ids = ids || [];
        const robotKeygen = (id: string | number) => `${this._tenantId}_Robot_${id}`;
        const robots = this._watchMap(this._robots, ids, robotKeygen);

        this._watch(robots.remove, false);
        this._watch(robots.add, true);

        this._robots = robots.watched as number[];
    }

    /**
     * Start/stop watching license groups
     * Removed/added groups are automatically determined
     * @param {(number[] | false)} ids
     * The license identifiers that need to be watched.
     * Passing a falsy value will terminate all active license subscriptions.
     * @memberof SignalRService
     */
    public watchLicenses(ids: string[] | false) {
        ids = ids || [];

        const licenseKeygen = (id: string | number) => `${this._tenantId}_License_${id}`;
        const licenses = this._watchMap(this._robots, ids, licenseKeygen);

        this._watch(licenses.remove, false);
        this._watch(licenses.add, true);
    }

    /**
     * Start/stop watching for newly added robots.
     *
     * @param {boolean} [watch=true]
     * Specifies if the subscription should be initiated/terminated
     * @memberof SignalRService
     */
    public watchNewRobots(watch = true) {
        this._watch([`${this._tenantId}_${this._folderId}_RobotsAdded`], watch);
    }

    /**
     * Listen for events from all robots.
     *
     * @param {boolean} [watch=true]
     * Specifies if the subscription should be initiated/terminated
     * @memberof SignalRService
     */
    public watchAllRobots(watch = true) {
        this._watch([`${this._tenantId}_${this._folderId}_RobotsAll`], watch);
    }

    /**
     * Start/stop watching job groups
     * Removed/added groups are automatically determined
     * @param {(number[] | false)} ids
     * The job identifiers that need to be watched.
     * Passing a falsy value will terminate all active job subscriptions.
     * @memberof SignalRService
     */
    public watchJobs(ids: number[] | false) {
        ids = ids || [];

        const jobKeygen = (id: string | number) => `${this._tenantId}_Job_${id}`;
        const jobs = this._watchMap(this._jobs, ids, jobKeygen);

        this._watch(jobs.remove, false);
        this._watch(jobs.add, true);

        this._jobs = jobs.watched as number[];
    }

    /**
     * Start/stop watching for newly added jobs.
     *
     * @param {boolean} [watch=true]
     * Specifies if the subscription should be initiated/terminated
     * @memberof SignalRService
     */
    public watchNewJobs(watch = true) {
        this._watch([`${this._tenantId}_${this._folderId}_JobsAdded`], watch);
    }

    /**
     * Listen for events from all jobs.
     *
     * @param {boolean} [watch=true]
     * Specifies if the subscription should be initiated/terminated
     * @memberof SignalRService
     */
    public watchAllJobs(watch = true) {
        this._watch([`${this._tenantId}_${this._folderId}_JobsAll`], watch);
    }

    /**
     * Start/stop watching for permission changes.
     *
     * @param {boolean} [watch=true]
     * Specifies if the subscription should be initiated/terminated
     * @memberof SignalRService
     */
    public watchPermissions(watch = true) {
        this._watch([`${this._tenantId}_PermissionsAll`], watch);
    }

    /**
     * Listen for events from all licenses.
     *
     * @param {boolean} [watch=true]
     * Specifies if the subscription should be initiated/terminated
     * @memberof SignalRService
     */
    public watchAllLicenses(watch = true) {
        this._watch([`${this._tenantId}_LicensesAll`], watch);
    }

    public monitoringConnect = (locale?: string, reconnect = false) => {
        if (
            this._appState.user.loggedInAsHost ||
            !this._appState.settings.isFeatureEnabled(FeatureFlag.Monitoring) ||
            !this._appState.user.folder.hasAny
        ) { return; }

        if (reconnect) {
            // synchronously stop the current SignalR socket
            this.monitoringDisconnect(false);
        }

        // setup the necessary headers for connect
        const headers: any = {
            [LOCALIZATION_HEADER_NAME]: locale,
        };

        if (this._appState.user.folder.hasAny) {
            headers[FOLDER_HEADER_NAME] = `${this._folderId}`;
        }
        jQuery.ajaxSetup({ headers });

        this._connections.monitoring.socket!.start(
            { transport: 'webSockets' },
            () => this._ngZone.run(() => {
                this._connections.callbacks.onStart(this._connections.monitoring);
            }),
        );
    }

    public monitoringDisconnect = (async = true) => {
        if (
            this._connections.monitoring.socket &&
            this._connections.monitoring.socket.state === SignalRState.Connected
        ) {
            this._connections.monitoring.isDisconnected = true;
            this._connections.monitoring.socket.stop(async);
        }
    }

    public monitoringSubscribe = (filters?: Record<string, any>) => {
        if (
            this._appState.user.loggedInAsHost ||
            !this._appState.settings.isFeatureEnabled(FeatureFlag.Monitoring)
        ) { return; }

        if (!!filters) {
            this._monitoringFilter = filters;
        }

        if (
            !this._connections.monitoring.isDisconnected &&
            this._connections.monitoring.socket &&
            this._connections.monitoring.socket.state === SignalRState.Connected
        ) {
            const options = {
                [FOLDER_PARAM_NAME]: this._folderId,
                [LOCALIZATION_PARAM_NAME]: this._translateService.currentLang,
            };

            this._connections.monitoring.proxy!.invoke('subscribeTo', options, this._monitoringFilter);
        }
    }

    /**
     * This method determines what ids have been added/removed from the previous watch.
     *
     * @private
     * @param {number[]|string[]} previous
     * What ids where previously watched.
     * @param {number[]|string[]} current
     * What ids need to be currently watched.
     * @param {(id: number|string) => string} keygen
     * The group id key generator
     * @returns
     * A map of groups that need to be added/removed from the signalR connection.
     * @memberof SignalRService
     */
    private _watchMap(previous: number[] | string[], current: number[] | string[], keygen: (id: number | string) => string) {
        const keep = intersection<number | string>(previous, current);
        const add = difference<number | string>(current, keep);
        const remove = difference<number | string>(previous, keep);

        const addGroups = add.map(keygen);
        const removeGroups = remove.map(keygen);

        return {
            add: addGroups,
            remove: removeGroups,
            watched: keep.concat(add),
        };
    }

    /**
     * Subscribes or unsubscribes from groups.
     *
     * @private
     * @param {string[]} groups
     * The groups that need to be sent.
     * @param {boolean} [subscribe=true]
     * If the groups need to be watched/unwatched.
     * @returns
     * @memberof SignalRService
     */
    private async _watch(groups: string[], subscribe = true) {
        if (
            !groups.length ||
            this._appState.user.loggedInAsHost
        ) { return; }

        const action = subscribe ? 'subscribe' : 'unsubscribe';
        const proxy = this._connections.browser.proxy;

        // if the connection is not initialized wait for the connection emitter
        if (
            this._connections.browser.isDisconnected ||
            (
                this._connections.browser.socket &&
                this._connections.browser.socket.state !== SignalRState.Connected
            )
        ) {
            this._connections.browser.connected$
                .pipe(
                    takeUntil(this._disconnected$),
                )
                .subscribe((success) => this._invoke(proxy, action, groups, success));
        } else {
            await this._invoke(proxy, action, groups);
        }

        // update the watched groups
        if (subscribe) {
            groups.forEach(e => this._watched.add(e));
        } else {
            groups.forEach(e => this._watched.delete(e));
        }
    }

    /**
     *
     *
     * @private
     * @param {*} proxy
     * The signalR proxy.
     * @param {('subscribe' | 'unsubscribe')} action
     * The action that needs to be performed.
     * @param {string[]} groups
     * The groups that need to be sent.
     * @param {boolean} [success=true]
     * The default success state
     * @returns
     * @memberof SignalRService
     */
    private _invoke(proxy: any, action: 'subscribe' | 'unsubscribe', groups: string[], success = true) {
        if (!success) { return; }

        return new Promise(resolve => {
            proxy.invoke(action, groups).done(() => {
                this._logger.log(`succesfully ${action}d the following groups: ${groups.join(',')}`);
                this._ngZone.run(() => resolve());
            });
        });
    }

    private _onBackOffReady = (connection: IConnection, delay: number) => {
        if (!connection.isDisconnected) {
            this._logger.error(`SignalR disconnected! Trying to reconnect in ${delay}ms!`);
            const attemptReconnect = connection.socket!.start();
            attemptReconnect.done(
                () => this._ngZone.run(() => this._connections.callbacks.onStart(connection)),
            );
        }
    }

    private _nextInZone = <T = void>(subject: Subject<T>) => (message: T) => this._ngZone.run(() => subject.next(message));
}
